import React from 'react';
import styled, { css } from 'styled-components';

import { useTranslation } from '@components/I18nProvider';
import { media } from '@/utils/mixin';
import { Stock } from '@/typings/model';
import { ReactComponent as UtmediaLogo } from '@/assets/svg/logo.svg';
import { LangType } from '@/typings/common';

type Props = {
  stock: Stock;
};

function formatNumber(num: number, lang: LangType) {
  const unitList =
    lang === 'ru'
      ? ['', 'тыс', 'млн', 'млрд', 'трлн']
      : ['', 'k', 'mln', 'bln', 'trln'];
  let fullNumber = num;
  let unitIndex = 0;

  while (fullNumber > 1000) {
    fullNumber = fullNumber / 1000;
    unitIndex += 1;
  }

  const fractionDigitCount = fullNumber < 10 ? 2 : fullNumber < 100 ? 1 : 0;

  return [fullNumber.toFixed(fractionDigitCount), unitList[unitIndex]].join();
}

function WidgetStockInvesting({ stock }: Props) {
  const { language, t } = useTranslation();

  return (
    <Investing>
      <Logo>{stock.logo ? <img src={stock.logo} /> : <UtmediaLogo />}</Logo>
      <Title>
        {t('promotions')} {stock.name}
      </Title>
      <ButtonBlock>
        <Button href={stock.url} target="_blank">
          {t('invest')}
        </Button>
      </ButtonBlock>
      <InvestParamsBlock>
        {stock.capitalization ? (
          <InvestParam>
            <ParamsTitle>{t('capitalization')}</ParamsTitle>
            <Params>{formatNumber(stock.capitalization, language)}$</Params>
          </InvestParam>
        ) : null}
        <InvestParam>
          <ParamsTitle>{t('changeOverTheYear')}</ParamsTitle>
          <PriceDelta up={stock.priceDelta >= 0 ? 'up' : 'down'}>
            {stock.priceDelta.toFixed(2)}%
          </PriceDelta>
        </InvestParam>
      </InvestParamsBlock>
    </Investing>
  );
}

const Investing = styled.div`
  background: var(--invest-bg-color);
  padding: 25px 20px;
  margin: 25px 0;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  text-align: center;
  ${media.mobile(css`
    margin-left: -15px;
    margin-right: -15px;
  `)}
`;
const Logo = styled.a`
  background-color: transparent;
  color: inherit;
  transition: 0.3s all ease;
  img,
  svg {
    width: 100px;
    height: auto;
  }
`;
const Title = styled.h3`
  display: block;
  font-size: 20px;
  margin: 20px 0;
  line-height: 24px;
  font-style: normal;
  font-weight: 700;
  width: 100%;
`;
const ButtonBlock = styled.div`
  margin-bottom: 20px;
  width: 100%;
  ${media.mobile(css`
    display: block;
  `)}
`;

const Button = styled.a`
  height: 50px;
  background: #ed4040;
  color: #fff;
  line-height: 50px;
  text-align: center;
  display: inline-block;
  font-size: 18px;
  padding: 0 40px;
  cursor: pointer;
  border-radius: 10px;
  ${media.mobile(css`
    margin-left: 0;
    width: 100%;
  `)}
  &:hover {
    background: #d52525;
  }
`;
const InvestParamsBlock = styled.div`
  display: flex;
`;
const InvestParam = styled.div`
  margin-right: 25px;
  span {
    font-size: 13px;
    white-space: nowrap;
    line-height: 100%;
    &:last-child {
      padding: 5px 10px;
      border-radius: 10px;
    }
  }
`;
const ParamsTitle = styled.span`
  display: block;
  color: #999;
  margin-bottom: 6px;
`;

const Params = styled.span`
  display: block;
  position: relative;
`;
const PriceDelta = styled.span<{ up?: 'up' | 'down' }>`
  display: block;
  position: relative;
  ${(props) =>
    props.up === 'up'
      ? css`
          width: 100%;
          background-color: rgba(49, 134, 238, 0.15);
          color: #3186ee;
          text-align: center;
          padding: 5px 10px;
          border-radius: 10px;
        `
      : css`
          background-color: rgba(211, 47, 47, 0.1);
          color: #d32f2f;
        `}
`;
export default WidgetStockInvesting;
